import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ReactComponent as ChevronLeft } from "@icons/arrow-left.svg";
import { ReactComponent as ChevronRight } from "@icons/arrow-right.svg";

export default function HeroSection({ slides = [] }) {
  const safeSlides = slides.length ? slides : [];
  const [heroIdx, setHeroIdx] = useState(0);
  const [heroArrowHover, setHeroArrowHover] = useState(null);
  const heroSwipeRef = useRef({ isDown: false, startX: 0, startY: 0 });

  useEffect(() => {
    if (heroIdx > safeSlides.length - 1) setHeroIdx(0);
  }, [heroIdx, safeSlides.length]);

  const heroNav = (dir) => {
    setHeroIdx((index) => Math.max(0, Math.min(safeSlides.length - 1, index + dir)));
  };

  const nextHeroSlide = useCallback(() => {
    if (!safeSlides.length) return;
    setHeroIdx((index) => (index + 1) % safeSlides.length);
  }, [safeSlides.length]);

  const startHeroSwipe = (event) => {
    if (event.target.closest("button, a")) return;
    heroSwipeRef.current = {
      isDown: true,
      startX: event.clientX,
      startY: event.clientY,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const endHeroSwipe = (event) => {
    if (!heroSwipeRef.current.isDown) return;

    const deltaX = event.clientX - heroSwipeRef.current.startX;
    const deltaY = event.clientY - heroSwipeRef.current.startY;
    heroSwipeRef.current.isDown = false;
    event.currentTarget.releasePointerCapture?.(event.pointerId);

    if (Math.abs(deltaX) < 60 || Math.abs(deltaX) < Math.abs(deltaY) * 1.25) return;

    setHeroIdx((index) => {
      if (deltaX < 0) return Math.min(safeSlides.length - 1, index + 1);
      return Math.max(0, index - 1);
    });
  };

  const heroArrowZoneStyle = (side, disabled) => ({
    position: "absolute",
    top: 0,
    bottom: 0,
    [side]: 0,
    width: 260,
    zIndex: 30,
    display: "flex",
    alignItems: "center",
    justifyContent: side === "left" ? "flex-start" : "flex-end",
    padding: "0 40px",
    pointerEvents: disabled ? "none" : "auto",
  });

  const heroArrowEdgeFadeStyle = (side, active) => ({
    position: "absolute",
    inset: 0,
    zIndex: 0,
    pointerEvents: "none",
    opacity: active ? 1 : 0,
    background:
      side === "left"
        ? "linear-gradient(90deg, rgba(0,0,0,0.36) 0%, rgba(0,0,0,0.18) 46%, rgba(0,0,0,0) 100%)"
        : "linear-gradient(270deg, rgba(0,0,0,0.36) 0%, rgba(0,0,0,0.18) 46%, rgba(0,0,0,0) 100%)",
    transition: "opacity 0.5s cubic-bezier(.22, 1, .36, 1)",
  });

  const heroArrowStyle = (disabled, side) => ({
    background: "rgba(110,110,110, 0.12)",
    backdropFilter: "blur(12px)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "#fff",
    width: 60,
    height: 80,
    borderRadius: 16,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    zIndex: 1,
    opacity: !disabled && heroArrowHover === side ? 1 : 0,
    pointerEvents: disabled ? "none" : "auto",
    transition: "opacity 0.35s ease",
  });

  return (
    <section className="home-hero"
      onPointerDown={startHeroSwipe}
      onPointerUp={endHeroSwipe}
      onPointerCancel={() => {
        heroSwipeRef.current.isDown = false;
      }}
      style={{
        position: "relative",
        width: "100%",
        height: "100vh",
        overflow: "hidden",
        backgroundColor: "#000",
        touchAction: "pan-y",
      }}
    >
      <div
        style={{
          display: "flex",
          transform: `translateX(-${heroIdx * 100}%)`,
          transition: "transform .8s cubic-bezier(.4, 0, .2, 1)",
          height: "100%",
        }}
      >
        {safeSlides.map((slide, index) => {
          const isActive = index === heroIdx;

          return (
            <div
              key={slide.id}
              style={{
                flexShrink: 0,
                width: "100%",
                height: "100%",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <img
                src={slide.img}
                alt={slide.title}
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center top",
                }}
              />
              <div className="home-hero-overlay"
                aria-hidden="true"
                style={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 5,
                  pointerEvents: "none",
                  background:
                    "linear-gradient(247.91deg, rgba(0, 0, 0, 0) 4.08%, rgba(0, 0, 0, 0) 50.97%, rgba(0, 0, 0, 0.0233106) 59.07%, rgba(0, 0, 0, 0.38) 77.66%)",
                }}
              />
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  width: "100%",
                  height: 232,
                  left: 0,
                  top: 0,
                  zIndex: 6,
                  pointerEvents: "none",
                  background:
                    "linear-gradient(180deg, rgba(0, 0, 0, 0) 25.71%, #000000 92.15%)",
                  transform: "matrix(1, 0, 0, -1, 0, 0)",
                }}
              />
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  width: "100%",
                  height: 232,
                  left: 0,
                  bottom: 0,
                  zIndex: 7,
                  pointerEvents: "none",
                  background:
                    "linear-gradient(180deg, rgba(0, 0, 0, 0) 42.84%, #000000 97.54%)",
                }}
              />
              <div className="home-hero-copy"
                style={{
                  position: "absolute",
                  bottom: "18%",
                  left: 56,
                  zIndex: 10,
                  maxWidth: 720,
                  opacity: isActive ? 1 : 0,
                  transform: isActive ? "translateY(0)" : "translateY(40px)",
                  transition: "opacity 0.8s ease-out, transform 0.8s ease-out",
                  transitionDelay: isActive ? "0.3s" : "0s",
                }}
              >
                {slide.logo ? (
                  <img
                    src={slide.logo}
                    alt={slide.title}
                    style={{
                      width: 440,
                      maxWidth: "42vw",
                      maxHeight: 220,
                      display: "block",
                      objectFit: "contain",
                      objectPosition: "left bottom",
                      filter: "drop-shadow(0 10px 20px rgba(0,0,0,0.5))",
                    }}
                  />
                ) : (
                  <h1
                    style={{
                      margin: 0,
                      color: "#fff",
                      fontSize: 72,
                      fontWeight: 700,
                      lineHeight: 1,
                      textShadow: "0 12px 30px rgba(0,0,0,0.65)",
                    }}
                    className="jost-s"
                  >
                    {slide.title}
                  </h1>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="home-hero-cta"
        style={{
          position: "absolute",
          bottom: "7%",
          left: 56,
          zIndex: 25,
        }}
      >
        <Link
          to={safeSlides[heroIdx]?.href || "/"}
          style={{
            background: "#354BCA",
            border: "none",
            color: "#fff",
            padding: "18px 16px",
            borderRadius: 16,
            fontSize: 16,
            fontWeight: 500,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            textDecoration: "none",
            boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
            transition: "transform 0.2s ease",
          }}
          className="jost-m"
        >
          Смотреть с подпиской
        </Link>
      </div>

      {[
        {
          dir: -1,
          icon: <ChevronLeft size={36} />,
          disabled: heroIdx === 0,
          side: "left",
        },
        {
          dir: 1,
          icon: <ChevronRight size={36} />,
          disabled: heroIdx === safeSlides.length - 1,
          side: "right",
        },
      ].map((button) => (
        <div className="home-hero-arrow"
          key={button.side}
          onMouseEnter={() => setHeroArrowHover(button.side)}
          onMouseLeave={() => setHeroArrowHover(null)}
          style={heroArrowZoneStyle(button.side, button.disabled)}
        >
          <div
            style={heroArrowEdgeFadeStyle(
              button.side,
              !button.disabled && heroArrowHover === button.side
            )}
          />
          <button
            onClick={() => heroNav(button.dir)}
            style={heroArrowStyle(button.disabled, button.side)}
          >
            {button.icon}
          </button>
        </div>
      ))}

      <div
        style={{
          position: "absolute",
          bottom: 40,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          gap: 6,
          zIndex: 30,
        }}
      >
        {safeSlides.map((_, index) => {
          const isActive = index === heroIdx;

          return (
            <div
              key={index}
              onClick={() => setHeroIdx(index)}
              style={{
                height: 6,
                borderRadius: 10,
                cursor: "pointer",
                overflow: "hidden",
                position: "relative",
                transition: "width .4s cubic-bezier(.4,0,.2,1)",
                width: isActive ? 30 : 6,
                background: "rgba(255,255,255,0.3)",
              }}
            >
              {isActive && (
                <div
                  key={heroIdx}
                  onAnimationEnd={nextHeroSlide}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    borderRadius: 10,
                    background: "#fff",
                    transformOrigin: "left center",
                    animation: "heroPaginationFill 5s linear forwards",
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes heroPaginationFill {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
      `}</style>
    </section>
  );
}
